/* eslint-disable */
// BBOT — Phase E: coach percentile/range search ("14U fastball velo ≥90th"). Reads the SAME percentile
// data the reports carry (report.percentiles, computed against the live reference) and renders each match
// in the SAME two-color seam-dot bar grammar (PercentilePeerBar) — one visual language everywhere.
// NON-REVOCABLE: a player appears ONLY if measured on that tool in that bracket; no bar = no claim.
import React, { useState } from "react";
import { PercentilePeerBar, searchPlayerMetric, PERCENTILE_METRICS, AGE_BRACKETS } from "./BbotPercentile";

const BLUE = "#2970b8", CORAL = "#e8526a", INK2 = "#6a7da6", HAIR = "#dbe6fb";
var fieldStyle = {width:"100%",padding:"8px 10px",border:"1px solid "+HAIR,borderRadius:7,fontSize:13,color:BLUE,background:"#fff"};
var labStyle = {font:"700 9.5px Inter,system-ui,sans-serif",color:INK2,letterSpacing:.5,display:"block",marginBottom:3};

export function BbotPercentileSearch({reports, users, go, init}){
  init = init || {};
  var [bracket, setBracket] = useState(init.bracket || "14U");
  var [metric, setMetric]   = useState(init.metric  || "pitchVeloPeak");
  var [minPct, setMinPct]   = useState(init.min != null ? init.min : 90);
  var [maxPct, setMaxPct]   = useState(init.max != null ? init.max : 99);

  var players = (users || []).filter(function(u){ return u.type === "player"; });
  var mn = Number(minPct), mx = Number(maxPct);
  var results = [];
  players.forEach(function(u){
    var s = searchPlayerMetric(reports, u.id, metric, bracket);   // his ceiling for this metric+bracket
    if(!s) return;                                                 // not measured -> not a result (no fabrication)
    if(s.pct >= mn && s.pct <= mx) results.push({u:u, s:s});
  });
  results.sort(function(a,b){ return b.s.pct - a.s.pct; });
  var metricLabel = (PERCENTILE_METRICS.filter(function(m){return m.key===metric;})[0] || {}).label || metric;
  var rangeTxt = (mx >= 99) ? ("≥ " + mn + "th") : (mn + "th–" + mx + "th");

  return (
    <div style={{maxWidth:680,margin:"0 auto",padding:"0 0 40px"}}>
      <div style={{marginBottom:14}}>
        <h2 style={{font:"800 24px Inter,system-ui,sans-serif",color:BLUE,letterSpacing:.5,margin:0}}>Percentile Search</h2>
        <p style={{font:"500 12px Inter,system-ui,sans-serif",color:INK2,margin:"4px 0 0"}}>Find players by measured standing against same-age peers. Only measured marks appear — never an estimate.</p>
      </div>

      {/* query row */}
      <div style={{display:"grid",gridTemplateColumns:"1.1fr 1.5fr 0.8fr 0.8fr",gap:10,alignItems:"end",background:"#f3f7ff",border:"1px solid "+HAIR,borderRadius:10,padding:"12px 14px",marginBottom:16}}>
        <div><label style={labStyle}>AGE BRACKET</label>
          <select value={bracket} onChange={function(e){setBracket(e.target.value);}} style={fieldStyle}>
            {AGE_BRACKETS.map(function(b){ return <option key={b} value={b}>{b}</option>; })}
          </select></div>
        <div><label style={labStyle}>METRIC</label>
          <select value={metric} onChange={function(e){setMetric(e.target.value);}} style={fieldStyle}>
            {/* rankable (directional) tools only — search ranks "≥90th", which is a betterness query.
               NEUTRAL measured tools (arm slot/stride/tempo/weight-shift/torso-tilt) show their
               distribution position on the report/profile bar but are never rank-searched here. */}
            {PERCENTILE_METRICS.filter(function(m){return m.rankable;}).map(function(m){ return <option key={m.key} value={m.key}>{m.label} ({m.src})</option>; })}
          </select></div>
        <div><label style={labStyle}>PCTILE ≥</label>
          <input type="number" min="20" max="99" value={minPct} onChange={function(e){setMinPct(e.target.value);}} style={fieldStyle}/></div>
        <div><label style={labStyle}>UP TO</label>
          <input type="number" min="20" max="99" value={maxPct} onChange={function(e){setMaxPct(e.target.value);}} style={fieldStyle}/></div>
      </div>

      {/* query summary + count */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:8}}>
        <div style={{font:"700 13px Inter,system-ui,sans-serif",color:BLUE}}>{bracket} · {metricLabel} · <span style={{color:CORAL}}>{rangeTxt} percentile</span></div>
        <div style={{font:"700 12px Inter,system-ui,sans-serif",color:INK2}}>{results.length} player{results.length===1?"":"s"}</div>
      </div>

      {/* results — each in the SAME bar grammar as the report */}
      {results.length === 0 ? (
        <div style={{padding:"22px 14px",textAlign:"center",font:"500 12px Inter,system-ui,sans-serif",color:INK2,border:"1px dashed "+HAIR,borderRadius:10}}>
          No players measured on {metricLabel} in {bracket} at {rangeTxt} percentile yet. As more sessions are evaluated, the reference and matches fill in.
        </div>
      ) : (
        results.slice(0,50).map(function(x){
          var u = x.u, s = x.s;
          var ident = [u.position, u.gradYear ? ("Class of "+u.gradYear) : null, (u.state||u.orgState)].filter(Boolean).join(" · ");
          return (
            <div key={u.id} onClick={function(){ if(go) go("profile-"+u.id); }} style={{border:"1px solid "+HAIR,borderRadius:10,padding:"9px 12px",marginBottom:8,background:"#fff",cursor:go?"pointer":"default"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:2}}>
                <span style={{font:"800 14px Inter,system-ui,sans-serif",color:BLUE}}>{(u.firstName||"")+" "+(u.lastName||"")}</span>
                <span style={{font:"600 10px Inter,system-ui,sans-serif",color:INK2}}>{ident}</span>
              </div>
              <PercentilePeerBar tool={s.tool} best={s.best} sess={s.sess} raw={s.raw} n={s.n}/>
            </div>
          );
        })
      )}
      <div style={{font:"500 9px Inter,system-ui,sans-serif",color:INK2,marginTop:10,lineHeight:1.5}}>
        Percentiles are vs same-age peers (point-in-time), shown as a range until the reference sample frees an exact mark. Ranked by each player's best-across-sessions mark; the smaller coral dot is their latest session.
      </div>
    </div>
  );
}

// ---------------- dev-only PREVIEW with multi-player sample data ----------------
function mkRep(id, playerId, key, pct, display, mode, n, raw, date){
  var mv = {}; mv[key] = raw;
  var metrics = {}; metrics[key] = {percentile:pct, display:display, mode:mode, sample_n:n};
  return {id:id, playerId:playerId, status:"approved", date:date, measuredValues:mv,
          percentiles:{bracket:"14U", version:1, metrics:metrics}};
}
const SAMPLE_USERS = [
  {id:1, type:"player", firstName:"Mateo",  lastName:"Rivera",   position:"RHP", gradYear:2029, state:"PR"},
  {id:2, type:"player", firstName:"Diego",  lastName:"Santiago", position:"RHP", gradYear:2029, state:"FL"},
  {id:3, type:"player", firstName:"Liam",   lastName:"Carter",   position:"LHP", gradYear:2030, state:"TX"},
  {id:4, type:"player", firstName:"Noah",   lastName:"Pérez",    position:"SS",  gradYear:2029, state:"PR"},
  {id:5, type:"player", firstName:"Eli",    lastName:"Brooks",   position:"RHP", gradYear:2029, state:"GA"},
];
const SAMPLE_REPORTS = [
  mkRep(101,1,"pitchVeloPeak",91,"91st","exact",300,"87","2026-05-01"), // latest (newer) = 91st
  mkRep(102,1,"pitchVeloPeak",94,"94th","exact",300,"88","2026-03-01"), // older = ceiling 94th -> two-mark (blue 94 + coral 91)
  mkRep(103,2,"pitchVeloPeak",90,"90th","exact",300,"86","2026-05-10"),
  mkRep(104,3,"pitchVeloPeak",60,"60-70th","range",95,"82","2026-05-12"), // below threshold -> excluded at >=90
  mkRep(105,5,"pitchVeloPeak",96,"96th","exact",300,"90","2026-04-20"),
  // player 4 (SS) has exit velo but NO fastball velo -> never appears in a fastball search (no fabrication)
  mkRep(106,4,"exitVeloPeak",88,"88th","exact",210,"92","2026-05-05"),
];
export function PercentileSearchPreview(){
  var q = (typeof window !== "undefined") ? window.location.search : "";
  function p(name){ var m = q.match(new RegExp(name+"=([^&]+)")); return m ? decodeURIComponent(m[1]) : null; }
  var init = {bracket:p("bracket")||"14U", metric:p("metric")||"pitchVeloPeak",
              min:p("min")!=null?Number(p("min")):90, max:p("max")!=null?Number(p("max")):99};
  return (
    <div style={{background:"#eef1f6",minHeight:"100vh",padding:"20px 16px"}}>
      <BbotPercentileSearch reports={SAMPLE_REPORTS} users={SAMPLE_USERS} init={init}/>
    </div>
  );
}
